import {
  Dialog,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { Stack, Typography } from "@mui/material";
import { ResendEmailRPC } from "@pulse/pulse-rpcs/lib/email-cycle/resendEmailRPC";
import { EmailStatus } from "@pulse/pulse-rpcs/lib/types/emailStatus";
import { AlertCircle, HelpCircle } from "lucide-react";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SuccessDialog } from "../../../../components/dialogs/SuccessDialog";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import { useRouteNavigator } from "../../../../routes/RoutesHelper";
import { useResendEmailStore } from "../../stores/Hooks";
import { ErrorDialog } from "../../../../components/dialogs/ErrorDialog";
import { EMPTY_CHARACTER } from "@pulse/shared-components";

interface ResendEmailDialogProps {
  emailId: string;
  recipientStatus: EmailStatus.EmailStatus;
  onCancel: VoidFunction;
  onCloseAfterResendAttempt: VoidFunction;
}

type DialogToOpen =
  | "CONFIRM_RESEND"
  | "SUCCESS"
  | "RESEND_NOT_ALLOWED"
  | "PROJECT_ARCHIVED_ERROR"
  | "UNEXPECTED_ERROR";

const CONFIRM_RESEND_DIALOG_WIDTH = "560px";
const CONFIRM_RESEND_DIALOG_DESCRIPTION_ICON = 20;

export const ResendEmailDialog: React.FC<ResendEmailDialogProps> = observer(
  ({ emailId, recipientStatus, onCancel, onCloseAfterResendAttempt }) => {
    const [dialogToOpen, setDialogToOpen] =
      useState<DialogToOpen>("CONFIRM_RESEND");

    const { t } = useTranslation();
    const spacing = useSpacing();
    const typography = useTypography();
    const tokens = useFoundationColorTokens();
    const navigateTo = useRouteNavigator();
    const resendEmailStore = useResendEmailStore();

    const showWarning =
      recipientStatus === EmailStatus.EmailStatus.BOUNCED ||
      recipientStatus === EmailStatus.EmailStatus.COMPLAINT;

    useEffect(() => resendEmailStore.resetStore, [resendEmailStore]);

    const getReasonString = (): string => {
      switch (recipientStatus) {
        case EmailStatus.EmailStatus.BOUNCED:
          return t("emailCycles.resendEmailWarningBounceReason");
        case EmailStatus.EmailStatus.COMPLAINT:
          return t("emailCycles.resendEmailWarningComplaintReason");
        default:
          return EMPTY_CHARACTER;
      }
    };

    const handleResendEmail = async (): Promise<void> => {
      try {
        await resendEmailStore.resendEmail(emailId);

        switch (resendEmailStore.resendEmailRPCError) {
          case null:
            setDialogToOpen("SUCCESS");
            break;
          case ResendEmailRPC.RPCError.InvalidEmailId:
            navigateTo.pageNotFound();
            break;
          case ResendEmailRPC.RPCError.ProjectAlreadyArchived:
            setDialogToOpen("PROJECT_ARCHIVED_ERROR");
            break;
          case ResendEmailRPC.RPCError.ResendNotAllowed:
            setDialogToOpen("RESEND_NOT_ALLOWED");
            break;
          case ResendEmailRPC.RPCError.UnexpectedError:
            setDialogToOpen("UNEXPECTED_ERROR");
            break;
          default:
            resendEmailStore.resendEmailRPCError satisfies never;
        }
      } catch {
        setDialogToOpen("UNEXPECTED_ERROR");
      }
    };

    const handleResendAttemptDialogClose = (): void => {
      resendEmailStore.resetStore();
      onCloseAfterResendAttempt();
    };

    return (
      <>
        <Dialog
          open={dialogToOpen === "CONFIRM_RESEND"}
          title={t("emailCycles.resendEmailDialogTitle")}
          primaryButtonText={t("emailCycles.resendEmail")}
          onPrimaryButtonClick={handleResendEmail}
          secondaryButtonText={t("common.cancel")}
          onSecondaryButtonClick={onCancel}
          isSecondaryButtonDisabled={resendEmailStore.loadingResendEmail}
          width={CONFIRM_RESEND_DIALOG_WIDTH}
          disableBackdropClick={true}
        >
          <Stack gap={spacing.spaceMD} width="100%">
            <Stack
              direction="row"
              alignItems="flex-start"
              gap={spacing.spaceXS}
              color={tokens.labelSubtle}
            >
              <HelpCircle
                size={CONFIRM_RESEND_DIALOG_DESCRIPTION_ICON}
                style={{ flexShrink: 0 }}
              />
              <Typography {...typography.b2}>
                {t("emailCycles.confirmResendEmailDialogDescription")}
              </Typography>
            </Stack>
            {showWarning && (
              <Stack
                direction="row"
                alignItems="flex-start"
                gap={spacing.spaceXS}
                color={tokens.labelWarning}
              >
                <AlertCircle
                  size={CONFIRM_RESEND_DIALOG_DESCRIPTION_ICON}
                  style={{ flexShrink: 0 }}
                />
                <Typography {...typography.b2}>
                  {t("emailCycles.resendBouncedEmailWarningMessage", {
                    reason: getReasonString(),
                  })}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Dialog>

        {dialogToOpen === "SUCCESS" && (
          <SuccessDialog
            title={t("emailCycles.resendEmailSuccessDialogTitle")}
            message={t("emailCycles.resendEmailSuccessDialogDescription")}
            onClickClose={handleResendAttemptDialogClose}
          />
        )}

        {dialogToOpen === "PROJECT_ARCHIVED_ERROR" && (
          <ErrorDialog
            title={t("common.projectAlreadyArchived")}
            message={t("common.projectAlreadyArchivedErrorText")}
            onClickClose={handleResendAttemptDialogClose}
          />
        )}

        {dialogToOpen === "RESEND_NOT_ALLOWED" && (
          <ErrorDialog
            title={t("emailCycles.resendNotAllowedErrorTitle")}
            message={t("emailCycles.resendNotAllowedErrorDescription")}
            onClickClose={handleResendAttemptDialogClose}
          />
        )}

        {dialogToOpen === "UNEXPECTED_ERROR" && (
          <UnexpectedErrorDialog
            message={t("emailCycles.resendEmailUnexpectedErrorMessage")}
            onClickDone={handleResendAttemptDialogClose}
          />
        )}
      </>
    );
  },
);
